import React from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Switch,
  Tooltip,
  useColorMode,
} from "@chakra-ui/react";
import { IoIosInformationCircleOutline } from "react-icons/io";

/**
 * Single-source styling + wording for the "should this action also adjust my
 * tracked account value?" toggle. Used across Add Trade, Close Trade, Edit
 * Trade (single + bulk), Delete Trade, Import CSV, and Bulk Update CSV.
 *
 * Props:
 * - isChecked / onChange: standard Chakra Switch hooks
 * - actionLabel: e.g. "this trade", "imported trades", "deleted trades" —
 *   inserted into the tooltip so the explanation stays contextual.
 * - ...rest: forwarded to the wrapping FormControl (mt, ml, etc.)
 */
const AccountValueSyncToggle = ({
  isChecked,
  onChange,
  actionLabel = "this action",
  id = "accountValueSyncToggle",
  ...rest
}) => {
  const { colorMode } = useColorMode();
  const labelColor = colorMode === "light" ? "gray.700" : "gray.200";
  const helpColor = colorMode === "light" ? "gray.500" : "gray.400";
  const tooltipText = `When enabled, ${actionLabel} adjusts your tracked account value on the close date.`;

  return (
    <FormControl display="flex" alignItems="center" {...rest}>
      <Switch
        id={id}
        size="sm"
        colorScheme="blue"
        isChecked={isChecked}
        onChange={onChange}
        mr={2}
      />
      <FormLabel htmlFor={id} mb={0} fontSize="sm" color={labelColor} cursor="pointer">
        <HStack spacing={1}>
          <span>Update account value</span>
          <Tooltip label={tooltipText} placement="top" hasArrow>
            <span>
              <Icon as={IoIosInformationCircleOutline} boxSize={3.5} color={helpColor} />
            </span>
          </Tooltip>
        </HStack>
      </FormLabel>
    </FormControl>
  );
};

export default AccountValueSyncToggle;
